import { Translation } from 'hooks/useTranslation/useTranslation';
import { FilterChip } from 'v2/components/SearchAndFilters/SearchAndFilters';

import {
  FilterAccess,
  FilterOrigin,
  FilterRole,
  FilterUserExpires,
  FilterUserExpiresValue,
  UsersFilterState
} from './userFilterTypes';

interface Params {
  translation: Translation;
  filters: UsersFilterState;
}

const getFilterAccessLabel = (key: keyof FilterAccess, translation: Translation): string => {
  switch (key) {
    case 'accessAppDisconnected':
      return translation.access_app_disconnected;
    case 'configAppDisconnected':
      return translation.config_app_disconnected;
    case 'noGroup':
      return translation.no_group;
    case 'hasCard':
      return translation.has_card;
    default:
      return translation.no_card;
  }
};

const getFilterUserExpiresLabel = (value: FilterUserExpiresValue, translation: Translation): string => {
  switch (value) {
    case FilterUserExpires.Expired:
      return translation.expired;
    case FilterUserExpires.OneDay:
      return translation.expires_in_24h;
    case FilterUserExpires.OneWeek:
      return translation.expires_this_week;
    default: {
      const exhaustiveCheck: never = value;
      return exhaustiveCheck;
    }
  }
};

const getFilterRoleLabel = (key: keyof FilterRole, translation: Translation): string => {
  switch (key) {
    case 'keyManager':
      return translation.key_manager;
    case 'administrator':
      return translation.administrator;
    default:
      return translation.installer;
  }
};

const getFilterOriginLabel = (key: keyof FilterOrigin, translation: Translation): string => {
  switch (key) {
    case 'entraId':
      return translation.entra_id;
    case 'atlas':
      return translation.atlas;
    default:
      return translation.key_sharing;
  }
};

export const createUserFilterChips = ({ filters, translation }: Params): FilterChip[] => {
  const { filterAccess, filterUserExpires, filterRole, filterOrigin } = filters.filter;
  const { setFilterAccess, setFilterUserExpires, setFilterRole, setFilterOrigin } = filters.setters;
  return [
    ...(Object.keys(filterAccess) as (keyof FilterAccess)[])
      .filter((key) => filterAccess[key])
      .map((key) => ({
        label: getFilterAccessLabel(key, translation),
        onDelete: () =>
          setFilterAccess((current) => ({
            ...current,
            [key]: false
          }))
      })),

    ...(filterUserExpires !== null
      ? [
          {
            label: getFilterUserExpiresLabel(filterUserExpires, translation),
            onDelete: () => setFilterUserExpires(null)
          }
        ]
      : []),

    ...(Object.keys(filterRole) as (keyof FilterRole)[])
      .filter((key) => filterRole[key])
      .map((key) => ({
        label: getFilterRoleLabel(key, translation),
        onDelete: () =>
          setFilterRole((current) => ({
            ...current,
            [key]: false
          }))
      })),

    ...(Object.keys(filterOrigin) as (keyof FilterOrigin)[])
      .filter((key) => filterOrigin[key])
      .map((key) => ({
        label: getFilterOriginLabel(key, translation),
        onDelete: () =>
          setFilterOrigin((current) => ({
            ...current,
            [key]: false
          }))
      }))
  ];
};
